import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState({
    company_name: 'HYPA MOTORS',
    company_tagline: 'Drive in Luxury',
    company_logo: '',
    company_logo_text: 'HM'
  });
  const location = useLocation();

  useEffect(() => {
    fetchSettings();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleScroll = () => {
    setScrolled(window.scrollY > 50);
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/cars', label: 'Vehicles' },
    { path: '/tours', label: 'Tours' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-gray-900 shadow-lg py-3' 
        : 'bg-transparent py-5'
    }`}>
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            {settings.company_logo ? (
              <img src={settings.company_logo} alt="Logo" className="h-10 w-auto" />
            ) : (
              <div className="bg-yellow-500 p-2 rounded-lg group-hover:rotate-6 transition-transform duration-300 w-10 h-10 flex items-center justify-center">
                <span className="text-gray-900 font-bold text-lg">{settings.company_logo_text || 'HM'}</span>
              </div>
            )}
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold tracking-tight text-white">
                {settings.company_name || 'HYPA MOTORS'}
              </h1>
              <p className="text-xs text-yellow-400">
                {settings.company_tagline || 'Drive in Luxury'}
              </p>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-medium transition duration-200 ${
                  location.pathname === link.path 
                    ? 'text-yellow-400' 
                    : 'text-white hover:text-yellow-400'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 rounded-full bg-yellow-400"></span>
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link 
              to="/contact" 
              className="px-5 py-2 rounded-full font-medium transition duration-300 bg-yellow-500 text-gray-900 hover:bg-yellow-400"
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="md:hidden p-2 rounded-lg transition text-white"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
        }`}>
          <div className={`py-4 space-y-2 rounded-lg ${scrolled ? 'bg-gray-800' : 'bg-gray-900/95 backdrop-blur-md'}`}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block py-2 px-4 rounded-lg transition ${
                  location.pathname === link.path 
                    ? 'bg-yellow-500 text-gray-900' 
                    : 'text-gray-200 hover:bg-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link 
              to="/contact" 
              className="block bg-yellow-500 text-gray-900 text-center py-2 rounded-lg mx-4 mt-3"
              onClick={() => setIsOpen(false)}
            >
              Contact
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;